import React, { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, Lock, Map as MapIcon, Radio, Unlock } from "lucide-react";
import { FAMILY_TONE } from "./vocab";
import { timeAgo } from "@/components/radar_sync/ReplicaBanner";

const DOT = { online: "bg-emerald-400", stale: "bg-amber-400", offline: "bg-rose-400", never: "bg-white/25" };
const TH = "label-xs px-3 py-2.5 text-left align-bottom whitespace-nowrap";
const STICKY_R = "px-3 py-2 sticky right-0 z-10 bg-[#0a0a0a] border-l border-white/5";

export const envUsing = (usage, cid) => (usage?.environments || []).filter((e) => e.contexts?.[cid]?.total > 0);

function Cell({ v, unknown, max, testid, client }) {
    if (unknown) return <td className="px-3 py-2 text-center font-mono text-xs text-white/30 bg-[repeating-linear-gradient(135deg,transparent,transparent_4px,rgba(255,255,255,0.03)_4px,rgba(255,255,255,0.03)_8px)]" data-testid={testid}>?</td>;
    if (!v?.total) return <td className="px-3 py-2 text-center font-mono text-xs text-white/15" data-testid={testid}>—</td>;
    const heat = 0.07 + 0.33 * Math.min(1, v.total / Math.max(max, 1));
    return (
        <td className={`px-3 py-2 text-right whitespace-nowrap transition-colors ${client ? "border-l border-white/[0.03]" : ""}`} style={{ backgroundColor: `rgba(124,58,237,${heat})` }} data-testid={testid}>
            <span className={`font-mono ${client ? "text-xs text-white/80" : "text-sm text-white"}`}>{v.total.toLocaleString()}</span>
            <span className="ml-1 text-[10px] text-white/45">· {v.valid.toLocaleString()} valid</span>
        </td>
    );
}

function EnvHeader({ env, open, onToggle, canExpand }) {
    const tid = `usage-env-${env.env_id}`;
    return (
        <th className={`${TH} min-w-[150px] border-l border-white/5`} data-testid={tid}>
            <button type="button" disabled={!canExpand} onClick={onToggle} className="group flex items-start gap-1.5 text-left disabled:cursor-default" data-testid={`${tid}-toggle`}>
                {canExpand ? (open ? <ChevronDown className="w-3.5 h-3.5 mt-0.5 text-white/60" /> : <ChevronRight className="w-3.5 h-3.5 mt-0.5 text-white/40 group-hover:text-white/80 transition-colors" />) : <span className="w-3.5" />}
                <span>
                    <span className="flex items-center gap-1.5 normal-case text-[12px] text-white/90 font-medium tracking-normal">
                        <span className={`w-1.5 h-1.5 rounded-full ${DOT[env.health] || DOT.never}`} />{env.name}
                        {env.is_self && <span className="rounded-sm border border-white/15 px-1 text-[9px] text-white/50">here</span>}
                    </span>
                    <span className="block normal-case tracking-normal text-[10px] text-white/40 mt-0.5" data-testid={`${tid}-reported`}>
                        {[env.role, env.channel].filter(Boolean).join(" · ")}{" · "}
                        {env.reported ? (env.is_self ? "live" : `reported ${timeAgo(env.reported_at)}`) : <span className="text-amber-200/80">no report — older build</span>}
                    </span>
                </span>
            </button>
        </th>
    );
}

function RetireCell({ ctx, total }) {
    if (!ctx.active) return <td className={`${STICKY_R} text-[10px] font-mono text-white/35`} data-testid={`usage-retire-${ctx.id}`}>retired</td>;
    const n = total?.environments?.length || 0;
    return (
        <td className={`${STICKY_R} whitespace-nowrap`} data-testid={`usage-retire-${ctx.id}`}>
            {n ? <span className="inline-flex items-center gap-1 text-[10px] font-mono text-amber-200"><Lock className="w-3 h-3" /> blocked · {n} env{n > 1 ? "s" : ""}</span>
                : <span className="inline-flex items-center gap-1 text-[10px] font-mono text-emerald-300/80"><Unlock className="w-3 h-3" /> free</span>}
        </td>
    );
}

export function ContextUsageMap({ usage, contexts, onAskReports }) {
    const [open, setOpen] = useState(() => new Set());
    const [usedOnly, setUsedOnly] = useState(false);
    const envs = usage?.environments || [];
    const max = useMemo(() => Math.max(1, ...envs.flatMap((e) => Object.values(e.contexts || {}).map((v) => v.total))), [envs]);
    const rows = (contexts || []).filter((c) => !usedOnly || usage?.totals?.[c.id]?.total);
    const toggle = (id) => setOpen((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
    const remote = envs.filter((e) => !e.is_self).length;

    return (
        <section className="bg-[#0a0a0a] border border-white/10 rounded-md mb-6 overflow-hidden" data-testid="ctx-usage-map">
            <div className="flex flex-wrap items-end justify-between gap-4 px-5 py-4 border-b border-white/5">
                <div>
                    <div className="flex items-center gap-2 text-sm font-medium"><MapIcon className="w-4 h-4 text-[#7C3AED]" /> Context usage map</div>
                    <p className="text-xs text-white/45 mt-1 max-w-2xl">
                        Documents carrying each context {usage?.network ? `in every environment (${envs.length})` : "in this environment"} · how many are still valid.
                        A context can only be retired once no environment has documents with it{usage?.clients_visible ? " — open an environment to see its clients." : "."}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-1.5 text-[11px] text-white/60 cursor-pointer"><input type="checkbox" checked={usedOnly} onChange={(e) => setUsedOnly(e.target.checked)} className="accent-[#7C3AED]" data-testid="usage-used-only" /> In use only</label>
                    {usage?.network && remote > 0 && (
                        <button onClick={onAskReports} className="inline-flex items-center gap-1.5 rounded border border-white/10 px-2.5 py-1 text-[11px] text-white/70 hover:bg-white/5 transition-colors" data-testid="usage-ask-reports">
                            <Radio className="w-3.5 h-3.5" /> Ask environments to report now
                        </button>
                    )}
                </div>
            </div>
            {usage?.error && <div className="px-5 py-2 text-[11px] text-rose-200 bg-rose-500/10 border-b border-rose-500/20" data-testid="usage-error">Usage could not be loaded — {usage.error}. The server still blocks a retire while documents use the context.</div>}
            {usage?.unreported?.length > 0 && (
                <div className="px-5 py-2 text-[11px] text-amber-100/90 bg-amber-400/5 border-b border-amber-400/15" data-testid="usage-unreported">
                    No usage report from {usage.unreported.join(", ")} (older build or never connected) — their documents cannot be checked and do not block a retire.
                </div>
            )}
            <div className="overflow-x-auto">
                <table className="w-full text-left">
                    <thead><tr className="border-b border-white/5">
                        <th className={`${TH} sticky left-0 bg-[#0a0a0a] z-10 min-w-[200px]`}>Context</th>
                        {usage?.network && <th className={`${TH} text-right`}>Network</th>}
                        {envs.map((e) => (
                            <React.Fragment key={e.env_id}>
                                <EnvHeader env={e} open={open.has(e.env_id)} onToggle={() => toggle(e.env_id)} canExpand={!!e.clients?.length} />
                                {open.has(e.env_id) && (e.clients || []).map((c) => (
                                    <th key={c.client_id} className="px-3 py-2.5 text-left align-bottom whitespace-nowrap text-[11px] font-normal text-white/60 bg-white/[0.02]" title={c.client_id} data-testid={`usage-client-${e.env_id}-${c.client_id}`}>{c.name}</th>
                                ))}
                            </React.Fragment>
                        ))}
                        <th className={`${TH} sticky right-0 z-10 bg-[#0a0a0a] border-l border-white/5`}>Retire</th>
                    </tr></thead>
                    <tbody>
                        {!usage && <tr><td colSpan={4} className="px-5 py-6 text-center text-xs text-white/40 font-mono">Loading usage…</td></tr>}
                        {usage && rows.map((c) => (
                            <tr key={c.id} className="border-t border-white/5 hover:bg-white/[0.02] transition-colors" data-testid={`usage-row-${c.id}`}>
                                <td className="px-3 py-2 sticky left-0 bg-[#0a0a0a] z-10">
                                    <span className={`px-1.5 py-0.5 rounded-sm border text-[11px] font-mono ${FAMILY_TONE[c.family] || FAMILY_TONE.other}`}>{c.id}</span>
                                    <span className="ml-2 text-xs text-white/50">{c.label}</span>
                                </td>
                                {usage.network && <Cell v={usage.totals?.[c.id]} max={max * 1.5} testid={`usage-total-${c.id}`} />}
                                {envs.map((e) => (
                                    <React.Fragment key={e.env_id}>
                                        <Cell v={e.contexts?.[c.id]} unknown={!e.reported} max={max} testid={`usage-cell-${e.env_id}-${c.id}`} />
                                        {open.has(e.env_id) && (e.clients || []).map((cl) => (
                                            <Cell key={cl.client_id} v={cl.contexts?.[c.id]} max={max} client testid={`usage-cell-${e.env_id}-${cl.client_id}-${c.id}`} />
                                        ))}
                                    </React.Fragment>
                                ))}
                                <RetireCell ctx={c} total={usage.totals?.[c.id]} />
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </section>
    );
}
